// @ts-nocheck — aspirational data shapes / API mismatch; tracked under Task #75 (type-safety debt)
import type { Permission, AdminRole } from '@shared/permissions';
import {
  PERMISSIONS,
  ROLE_PERMISSIONS,
  hasPermission as sharedHasPermission,
  hasAnyPermission as sharedHasAnyPermission,
  hasAllPermissions as sharedHasAllPermissions,
  getRolePermissions,
  isValidAdminRole,
} from '@shared/permissions';

import type { AdminPageConfig } from '@/config/adminPageRegistry';

export type PermissionsSource = 'db' | 'fallback' | undefined;

function useDbPermissions(dbPermissions: string[] | undefined, source: PermissionsSource): boolean {
  return source === 'db' && Array.isArray(dbPermissions);
}

export function getUserPermissions(role: AdminRole | string, dbPermissions?: string[], source?: PermissionsSource): Permission[] {
  if (useDbPermissions(dbPermissions, source)) {
    return dbPermissions as Permission[];
  }
  if (!isValidAdminRole(role)) {
    return [];
  }
  return getRolePermissions(role);
}

export function checkPermission(userRole: AdminRole | string, permission: Permission, dbPermissions?: string[], source?: PermissionsSource): boolean {
  if (useDbPermissions(dbPermissions, source)) {
    if (dbPermissions!.includes('system.super_admin')) return true;
    return dbPermissions!.includes(permission);
  }
  return sharedHasPermission(userRole, permission);
}

export function checkAnyPermission(userRole: AdminRole | string, permissions: Permission[], dbPermissions?: string[], source?: PermissionsSource): boolean {
  if (useDbPermissions(dbPermissions, source)) {
    if (dbPermissions!.includes('system.super_admin')) return true;
    return permissions.some(p => dbPermissions!.includes(p));
  }
  return sharedHasAnyPermission(userRole, permissions);
}

export function checkAllPermissions(userRole: AdminRole | string, permissions: Permission[], dbPermissions?: string[], source?: PermissionsSource): boolean {
  if (useDbPermissions(dbPermissions, source)) {
    if (dbPermissions!.includes('system.super_admin')) return true;
    return permissions.every(p => dbPermissions!.includes(p));
  }
  return sharedHasAllPermissions(userRole, permissions);
}

export function filterPagesByPermission(
  pages: AdminPageConfig[],
  userRole: AdminRole | string,
  dbPermissions?: string[],
  source?: PermissionsSource
): AdminPageConfig[] {
  if (!userRole) {
    return [];
  }

  const userPermissions = getUserPermissions(userRole, dbPermissions, source);
  if (userPermissions.includes(PERMISSIONS.SYSTEM_SUPER_ADMIN as string)) {
    return pages.filter(page => page.enabled);
  }

  return pages.filter(page => {
    if (!page.enabled) {
      return false;
    }

    if (!page.requiredPermission) {
      return true;
    }

    if (Array.isArray(page.requiredPermission)) {
      return checkAnyPermission(userRole, page.requiredPermission, dbPermissions, source);
    }

    return checkPermission(userRole, page.requiredPermission, dbPermissions, source);
  });
}

export function canAccessPage(
  page: AdminPageConfig,
  userRole: AdminRole | string,
  dbPermissions?: string[],
  source?: PermissionsSource
): boolean {
  if (!page.enabled) {
    return false;
  }

  if (!userRole) {
    return false;
  }

  const userPermissions = getUserPermissions(userRole, dbPermissions, source);
  if (userPermissions.includes(PERMISSIONS.SYSTEM_SUPER_ADMIN as string)) {
    return true;
  }

  if (!page.requiredPermission) {
    return true;
  }

  if (Array.isArray(page.requiredPermission)) {
    return checkAnyPermission(userRole, page.requiredPermission, dbPermissions, source);
  }

  return checkPermission(userRole, page.requiredPermission, dbPermissions, source);
}

export function getPermittedNavigationGroups(
  pages: AdminPageConfig[],
  userRole: AdminRole | string,
  dbPermissions?: string[],
  source?: PermissionsSource
) {
  const permittedPages = filterPagesByPermission(pages, userRole, dbPermissions, source);
  
  const groupedPages = permittedPages.reduce((acc, page) => {
    if (!acc[page.category]) {
      acc[page.category] = [];
    }
    acc[page.category].push(page);
    return acc;
  }, {} as Record<string, AdminPageConfig[]>);

  return groupedPages;
}

const ADMIN_ROLE_SET: AdminRole[] = ['super_admin', 'admin', 'manager', 'operator', 'finance'];

export function isAdmin(userRole: AdminRole | string): boolean {
  return ADMIN_ROLE_SET.includes(userRole as AdminRole);
}

export function isSuperAdmin(userRole: AdminRole | string): boolean {
  return userRole === 'super_admin';
}

export type { Permission, AdminRole } from '@shared/permissions';
export { PERMISSIONS, ROLE_PERMISSIONS } from '@shared/permissions';
